import ProductCell from "@/core/components/ProductCell";
import { renderProductOption } from "@/features/product/components/ProductAutocompleteOption";
import { useProductAutocomplete } from "@/features/product/usecases/useProductAutocomplete";
import type { Product } from "@/generated/graphql";
import {
	type AutocompleteFetcher,
	Button,
	Collapsible,
	CollapsibleContent,
	ForwardedRefAutocomplete,
	Input,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
	cn
} from "@k3-tech/react-kit";
import { PlusIcon, TrashIcon } from "lucide-react";
import { useRef, useState } from "react";
import type { PricelistProductAddPriceFormData } from "../types/PricelistProductAddPriceFormData";

export default function PricelistProductForm({
	onSubmit,
	onCancel,
}: {
	pricelistID: string;
	pricelistPeriodID: string;
	onSubmit: (formData: { items: PricelistProductAddPriceFormData[] }) => void;
	onCancel: () => void;
}) {
	const autocompleteRef = useRef<{ reset?: () => void }>(null);

	const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
	const [productPrices, setProductPrices] = useState<
		PricelistProductAddPriceFormData[]
	>([]);

	const { load: loadProduct } = useProductAutocomplete();
	const productAutocompleteFetcher: AutocompleteFetcher<Product> = async (
		params,
	) => {
		// Consignment products are priced from the vendor consignment price and cannot be added to a pricelist.
		return loadProduct(params.page, params.pageSize, params.search, () => ({
			isConsignment: false,
		}));
	};

	const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		onSubmit({ items: productPrices });
		return false;
	};

	const handleSelectProduct = (product: Product) => {
		setSelectedProduct(product);
	};

	const handleAddProductPrice = () => {
		if (!selectedProduct) return;
		if (productPrices.find((x) => x.productID === selectedProduct.productID))
			return;
		setProductPrices((prev) => {
			prev.push({
				productID: selectedProduct.productID,
				productCode: selectedProduct.productCode || "-",
				productName: selectedProduct.productName || "-",
				barcode: selectedProduct.barcode,
				sellingPrice: 0,
			});
			return prev;
		});
		autocompleteRef.current?.reset?.();
		setSelectedProduct(null);
	};

	const handleRemoveProductPrice = (itemID: string) => () => {
		setProductPrices((prev) => [
			...prev.filter((item) => item.productID !== itemID),
		]);
	};

	const handleChangeFields = (
		item: PricelistProductAddPriceFormData,
		key: keyof PricelistProductAddPriceFormData,
		value: unknown,
	) => {
		setProductPrices((prev) => {
			const index = prev.findIndex((val) => val.productID === item.productID);
			if (index < 0) return prev;
			prev[index] = { ...prev[index], [key]: value };
			return prev;
		});
	};

	return (
		<>
			<div className="flex items-center justify-between gap-x-4">
				<div className="w-[512px]">
					<ForwardedRefAutocomplete<Product>
						ref={autocompleteRef}
						mode="server"
        				options={[]}
						fetcher={productAutocompleteFetcher}
						renderOption={renderProductOption}
						onChange={(_, options) => {
							if (options && !Array.isArray(options) && options.raw) {
								handleSelectProduct(options.raw);
							}
						}}
					/>
				</div>
				<Button
					type="button"
					className="cursor-pointer"
					onClick={handleAddProductPrice}
				>
					<PlusIcon />
				</Button>
			</div>
			<form onSubmit={handleSubmit}>
				<div className="relative mb-8 overflow-hidden rounded-md border border-border">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead className="min-w-[300px]">Product</TableHead>
								<TableHead>Selling Price</TableHead>
								<TableHead>Actions</TableHead>
								{/* <TableHead>&nbsp;</TableHead> */}
							</TableRow>
						</TableHeader>
						<TableBody>
							{productPrices.map((item, i) => (
								<Collapsible key={i.toString()} asChild>
									<>
										<TableRow>
											<TableCell>
												<ProductCell
													productCode={item.productCode}
													productName={item.productName}
													barcode={item.barcode}
												/>
											</TableCell>
											<TableCell>
												<Input
													type="number"
													onChange={(e) => {
														handleChangeFields(
															item,
															"sellingPrice",
															e.currentTarget.valueAsNumber,
														);
													}}
												/>
											</TableCell>
											<TableCell>
												<div className="flex items-center gap-x-2">
													<Button
														type="button"
														variant="destructive"
														onClick={handleRemoveProductPrice(item.productID)}
													>
														<TrashIcon />
													</Button>
												</div>
											</TableCell>
											{/* <TableCell>
												<CollapsibleTrigger asChild>
													<Button type="button" variant="ghost">
														<ChevronDownIcon />
													</Button>
												</CollapsibleTrigger>
											</TableCell> */}
										</TableRow>
										<CollapsibleContent asChild>
											<TableRow>
												<TableCell colSpan={4}>TEST</TableCell>
											</TableRow>
										</CollapsibleContent>
									</>
								</Collapsible>
							))}
						</TableBody>
					</Table>
				</div>

				<div className={cn("flex flex-col sm:flex-row gap-3 justify-end")}>
					<Button type="submit" className="sm:order-last">
						Save
					</Button>

					<Button type="button" variant="outline" onClick={onCancel}>
						Cancel
					</Button>
				</div>
			</form>
		</>
	);
}
